import React, { useState } from 'react'
import { useAtom } from 'jotai'
import { createUseStyles } from 'react-jss'
import { Button } from '../../../ui/Button/Button'
import { IkonKnapreminder } from '../../../../assets/icons'
import { CaseDetail } from '../../../../models/CaseDetail'
import { Typography } from '../../../ui/Typography/Typography'
import { colors } from '../../../../theme/palette'
import { Overlay } from '../../../ui/Sidebar'
import {
    control,
    cardProps,
    contentWrapper,
} from '../../../../theme/reuseables'
import { ReminderCard } from './ReminderCard'
import { CreateReminderView } from './CreateReminder'
import { L } from '../../../providers/Lokalise'
import { profileAtom } from '../../../../api/profile/profile'
import { RestrictElementWithTooltip } from '../RestrictElementWithTooltip'

type RemindersProps = {
    currentCase: CaseDetail
    onUpdated?: () => void
}

const useStyles = createUseStyles({
    control: {
        ...control,
    },
    card: {
        ...cardProps.card,
    },
    title: {
        ...cardProps.title,
    },
    contentWrapper: {
        ...contentWrapper,
    },
})

export type RemindersHeaderType = {
    name: string
    caseNumber: string
}

export const RemindersHeaderInfo = ({
    name,
    caseNumber,
}: RemindersHeaderType): JSX.Element => {
    const classes = useStyles()

    return (
        <div>
            <div className={classes.control}>
                <div>
                    <Typography tag="h4" size="XXS" color="rgba(0, 0, 0, 0.4)">
                        {L('LØN_SAGS_REMINDERS_HEADER_NYANSAT_LABEL')}
                    </Typography>
                    <Typography tag="p" size="XS">
                        {name}
                    </Typography>
                </div>
                <div>
                    <Typography tag="h4" size="XXS" color=" rgba(0, 0, 0, 0.4)">
                        {L('LØN_SAGS_REMINDERS_HEADER_SAGSNR_LABEL')}
                    </Typography>
                    <Typography tag="p" size="XS">
                        {caseNumber}
                    </Typography>
                </div>
            </div>
        </div>
    )
}

const Reminders: React.FC<RemindersProps> = ({ currentCase, onUpdated }) => {
    const [isOpen, setIsOpen] = useState(false)
    const [viewState, setViewState] = useState<'default' | 'create'>('default')
    const [authProfile] = useAtom(profileAtom)

    return (
        <>
            <RestrictElementWithTooltip
                restrictedAction={
                    !currentCase.allowedUpdateActionList.includes(
                        'Sagsbehandle'
                    )
                }
            >
                <Button
                    type="secondary"
                    onClick={() => {
                        if (
                            currentCase.reminderList.length === 0 &&
                            !authProfile.isImitator
                        ) {
                            setViewState('create')
                            setIsOpen(true)
                        } else {
                            setIsOpen(true)
                        }
                    }}
                    size="medium"
                    style={{
                        paddingLeft: '.5rem',
                        paddingRight: '.5rem',
                        width: '100%',
                    }}
                    disabled={
                        authProfile.isImitator ||
                        !currentCase.allowedUpdateActionList.includes(
                            'Sagsbehandle'
                        )
                    }
                >
                    <span
                        style={{
                            marginRight: '.5rem',
                            display: 'flex',
                            height: '100%',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <IkonKnapreminder fill={colors.green} width="16px" />
                    </span>

                    <span style={{ marginRight: '.5rem' }}>
                        {`${L('LØN_SAGS_REMINDERS_KNAP_PRE_LABEL')} (${
                            currentCase.reminderList.length ?? 0
                        })`}
                    </span>
                </Button>
            </RestrictElementWithTooltip>
            <Overlay
                isShowing={isOpen}
                hide={() => {
                    setViewState('default')
                    setIsOpen(false)
                }}
            >
                <RemindersHeaderInfo
                    name={currentCase.newlyHired.name}
                    caseNumber={currentCase.caseNumber}
                />
                {viewState === 'default' && (
                    <div
                        style={{
                            padding: '100px 20px 20px',
                            overflow: 'auto',
                            height: '100%',
                            paddingBottom: '80px',
                        }}
                    >
                        <Typography
                            tag="h2"
                            size="L"
                            align="center"
                            weight="bold"
                            style={{ margin: '20px 0' }}
                        >
                            {L('LØN_SAGS_REMINDERS_DIALOG_TITEL')}
                        </Typography>
                        <div
                            style={{
                                marginBottom: '2rem',
                                display: 'flex',
                                justifyContent: 'end',
                            }}
                        >
                            <Button
                                onClick={() => setViewState('create')}
                                size="medium"
                                disabled={
                                    authProfile.isImitator ||
                                    !currentCase.allowedUpdateActionList.includes(
                                        'Sagsbehandle'
                                    )
                                }
                            >
                                {L('LØN_SAGS_REMINDERS_OPRET_KNAP')}
                            </Button>
                        </div>
                        {currentCase.reminderList.map((reminder) => {
                            return (
                                <ReminderCard
                                    key={reminder.id}
                                    reminder={reminder}
                                    currentCase={currentCase}
                                    onUpdated={() => {
                                        setViewState('default')
                                        onUpdated?.()
                                    }}
                                />
                            )
                        })}
                    </div>
                )}
                {viewState === 'create' && (
                    <CreateReminderView
                        caseDetail={currentCase}
                        onClickCancel={() => {
                            if (currentCase.reminderList.length === 0) {
                                setIsOpen(false)
                            }

                            setViewState('default')
                        }}
                        onUpdated={() => {
                            setViewState('default')
                            setIsOpen(false)
                            onUpdated?.()
                        }}
                        isOpen={viewState === 'create'}
                    />
                )}
            </Overlay>
        </>
    )
}

export default Reminders
